import { format } from "@cloudinary/url-gen/actions/delivery";
import { auto } from "@cloudinary/url-gen/qualifiers/format";
import { formatISO } from "date-fns";
import { useMemo } from "react";

import { GAME_FALLBACK_IMAGE_ID } from "@/components/cloudinary-image";
import type { ReviewType } from "@/components/review-modal/types";
import { cloudinaryImageUrl } from "@/helpers/cloudinary-url";
import { datetimeToJS } from "@/helpers/parsers";
import useCloudinary from "@/hooks/use-cloudinary";

import type { GameType } from "../types";

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      telephone: "+1-833-987-1999",
    },
    logo: `${process.env.APP_URL}/morty.png`,
    name: "Morty",
    sameAs: [
      "https://www.facebook.com/mortyapp/",
      "https://twitter.com/mortyapp",
      "https://www.instagram.com/mortyapp/",
      "https://www.tiktok.com/@mortyapp",
    ],
    url: process.env.APP_URL,
  };
}

export function reviewSchema(reviews: ReviewType[]) {
  const ratingToNumber: Record<string, number> = {
    LIKE: 4,
    LOVE: 5,
  };

  return reviews
    .filter(
      (r) => ["LIKE", "LOVE"].includes(r.overallRating || "") && !!r.summary
    )
    .slice(0, 5)
    .map((r) => ({
      "@type": "Review",
      author: {
        "@type": "Person",
        name: r.user.username,
      },
      datePublished: formatISO(datetimeToJS(r.createdAt), {
        representation: "date",
      }),
      reviewBody: r.summary,
      reviewRating: {
        "@type": "Rating",
        bestRating: 5,
        ratingValue: ratingToNumber[r.overallRating as string],
        worstRating: 1,
      },
    }));
}

export function useGameSchema(
  game: GameType,
  reviewCount: number,
  reviews: ReviewType[]
) {
  const cloudinary = useCloudinary();

  // biome-ignore lint/correctness/useExhaustiveDependencies: no need cloudinary to re-render
  const image = useMemo(() => {
    const img = (game.gameimageSet || []).find((i) => !!i.imageId);
    return cloudinary
      .image(img?.imageId || GAME_FALLBACK_IMAGE_ID)
      .delivery(format(auto()))
      .toURL();
  }, [game]);

  // biome-ignore lint/suspicious/noExplicitAny: legacy
  const schema: Record<string, any> = {
    "@context": "https://schema.org/",
    "@type": "Product",
    description: game.description,
    image,
    name: game.name,
  };

  if (game.communityRatingCount) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingCount: game.communityRatingCount,
      ratingValue: game.communityScoreScaled,
      reviewCount,
    };
  }

  const review = reviewSchema(reviews);

  if (review) {
    schema.review = review;
  }

  return schema;
}

export function getGameSchema(
  game: GameType,
  reviewCount: number,
  reviews: ReviewType[]
) {
  const img = (game.gameimageSet || []).find((i) => !!i.imageId);
  const image = cloudinaryImageUrl(img?.imageId || GAME_FALLBACK_IMAGE_ID);

  // biome-ignore lint/suspicious/noExplicitAny: legacy
  const schema: Record<string, any> = {
    "@context": "https://schema.org/",
    "@type": "Product",
    description: game.description,
    image,
    name: game.name,
  };

  if (game.communityRatingCount) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingCount: game.communityRatingCount,
      ratingValue: game.communityScoreScaled,
      reviewCount,
    };
  }

  const review = reviewSchema(reviews);

  if (review) {
    schema.review = review;
  }

  return schema;
}
